import type { HostApi, PluginModule } from '@easydb/shared';
import { parseSecrets, readSecretsText, writeSecretsText } from '../db/user-settings.js';
import { GRID_SETTINGS_ID, WINDOW_ROWS_FROM_DEFAULT } from '../table/grid-settings.js';
import { LINK_PROTOCOLS_KEY, LINK_SETTINGS_ID } from '../util/link-settings.js';
import { DEFAULT_PROTOCOLS } from '../util/url-schemes.js';
import { DEFAULT_TILE_ATTRIBUTION, DEFAULT_TILE_URL, VIZ_SETTINGS_ID } from '../viz/viz-settings.js';
import { WINDOW_COLORS_KEY, WINDOWS_SETTINGS_ID } from '../window-mgr/window-color-settings.js';
import { DEFAULT_WINDOW_COLOR_LIST } from '../window-mgr/window-color.js';

export const meta: NonNullable<PluginModule['meta']> = {
  id: 'settings',
  name: 'Settings',
  type: 'ui',
  // Core UX surface — always on, never shown with a disable toggle. The Plugin
  // Manager hides fixed plugins unless the "Fixed" filter is explicitly on.
  fixed: true,
  version: '0.1.0',
  description: 'Header gear button that opens the tabbed Settings dialog; imports dropped secrets.txt.',
  author: 'Marc Cawood',
  icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
  repo: 'https://github.com/cawoodm/easydbaccess/blob/main/packages/renderer/src/plugins/settings.ts',
};

export function init(api: HostApi): void {
  api.ui.registerSettings(GRID_SETTINGS_ID, 'Table grid', [
    {
      key: 'sortDescFirst',
      label: 'Sort descending first',
      type: 'boolean',
      default: true,
      scope: 'workspace',
      description:
        'Clicking a column header sorts descending, then ascending, then off. Turn this off to start ascending. Dates, scores and counts are usually read from the high end down, which took two clicks before.',
    },
    {
      key: 'defaultSubstring',
      label: 'Default to substring',
      type: 'boolean',
      default: true,
      scope: 'workspace',
      description:
        'A filter value with no wildcard and no quotes matches any cell CONTAINING it. Turn this off to make it match the whole cell instead, which is what a list of values usually means. Either way `*foo*` asks for a substring and `"foo bar"` asks for the whole cell, so both are explicit. This setting has no effect on a NUMBER column: a bare value there always means EQUALS, never substring, so `180` never matches `1800`. Workspace-wide rather than per device, because it decides what the filters saved in this workspace mean.',
    },
    {
      key: 'highlightNulls',
      label: 'Highlight empty cells',
      type: 'boolean',
      default: true,
      scope: 'workspace',
      description:
        'An empty cell gets a pink background, so a gap in the data is visible whatever the column draws. Turn it off for a table that is mostly empty on purpose, where the colour is noise. A value that does not fit its column type stays marked red either way.',
    },
    {
      key: 'highlightErrors',
      label: 'Highlight cells Validate flagged',
      type: 'boolean',
      default: true,
      scope: 'workspace',
      description:
        'After the ✓ button checks a table, every cell that broke a rule gets the same pink background, and the reason is in its tooltip. Turn the colour off to read the reasons on hover alone; the marks go away by themselves when the next run finds nothing.',
    },
    {
      key: 'windowRowsFrom',
      label: 'Read big tables one page at a time (rows)',
      type: 'number',
      default: WINDOW_ROWS_FROM_DEFAULT,
      scope: 'workspace',
      description:
        'A table with at least this many rows is read one page at a time as you scroll, instead of being held in memory whole. Filtering, searching and sorting still cover every row, because the store does that work. Set 0 to always read the whole table — which stops at 20,000 rows and says so, so the default is 20,000: from there up, scrolling reaches every row instead.',
    },
  ]);

  api.ui.registerSettings(LINK_SETTINGS_ID, 'Links', [
    {
      key: LINK_PROTOCOLS_KEY,
      label: 'Protocols that may be links',
      type: 'string',
      default: DEFAULT_PROTOCOLS,
      // Device-local, and deliberately so: which protocols this machine is willing
      // to put behind a click is a decision about the machine, not about the data.
      // On the workspace layer it would travel inside a shared `.edb`, and opening
      // someone's workspace could widen your own rules.
      scope: 'user',
      description:
        'A list of protocols means those are the only links — e.g. "http,https,ftp,file". Start the list with ! and it says what to refuse instead, allowing everything else: the default "!javascript,vbscript,data" is the three that run code rather than going anywhere. Applies everywhere a link is drawn: Link columns, Markdown, HTML and view templates.',
      help: 'Leave it empty to go back to the default. Removing javascript, vbscript or data from the refused list lets a value someone else wrote — from an import, a sync pull, a workspace you were sent — run code in your session when you click it. Anything with no protocol at all, like /a/b or #section, is unaffected either way.',
    },
  ]);

  api.ui.registerSettings(WINDOWS_SETTINGS_ID, 'Windows', [
    {
      key: WINDOW_COLORS_KEY,
      label: 'Colours a window can be painted',
      type: 'string',
      default: DEFAULT_WINDOW_COLOR_LIST,
      // Workspace-scoped, unlike the Links list: the colour each window is
      // painted is stored with the workspace, so the palette it was picked from
      // belongs there too. A workspace opened on another machine then offers the
      // same choices its windows already use.
      scope: 'workspace',
      description:
        'What the palette button in a window\'s title bar offers. Hex values or HTML colour names, separated by commas — e.g. "#FF00DD,red,blue". Anything that is not a colour is ignored, so one typo costs only its own entry. Empty goes back to the nine shipped colours.',
      help: 'The title text is white, so a light colour is hard to read — the shipped nine are all dark enough. A colour you remove is still shown by any window already painted with it; the list says what can be chosen from now on.',
    },
  ]);

  // Visualization settings live here for the same reason the grid's do: this
  // plugin owns the Settings tab, and the elements that READ these values must
  // not import a plugin. See `viz/viz-settings.ts`.
  api.ui.registerSettings(VIZ_SETTINGS_ID, 'Visualizations', [
    {
      key: 'tileUrl',
      label: 'Map tile URL template',
      type: 'string',
      default: DEFAULT_TILE_URL,
      scope: 'workspace',
      description:
        'Where map visualizations fetch their background tiles. The default is OpenStreetMap, whose tile policy asks that heavy or commercial use runs its own server — point this at that server, or at a local one for an offline install. A map still plots its points when tiles cannot be loaded.',
      helpUrl: 'https://operations.osmfoundation.org/policies/tiles/',
      helpLinkLabel: 'OpenStreetMap tile usage policy',
    },
    {
      key: 'tileAttribution',
      label: 'Map attribution',
      type: 'string',
      default: DEFAULT_TILE_ATTRIBUTION,
      scope: 'workspace',
      description: 'Credit shown in the map corner. Most tile providers require this.',
    },
    {
      key: 'cloudMinLength',
      label: 'Word cloud: ignore words shorter than',
      type: 'number',
      default: 3,
      scope: 'workspace',
      description: 'A new word cloud starts with this. Three suits prose; raise it for noisy text, lower it for a column of codes.',
    },
    {
      key: 'cloudKeepWords',
      label: 'Word cloud: always keep these words',
      type: 'text',
      scope: 'workspace',
      description:
        'The exception to the length limit above — and to the ignore list below. Acronyms are often the most interesting terms in a column and the first thing a length limit throws away: AI, UI, CH, SQL. Separate with commas, spaces or new lines.',
    },
    {
      key: 'cloudStopWords',
      label: 'Word cloud: ignore these common words',
      type: 'text',
      scope: 'workspace',
      description:
        'Words too common to be interesting. Left as shipped this is an English function-word list, which is what stops "the" being the biggest word in every cloud — replace it wholesale for another language, or clear it to count everything. Separate with commas, spaces or new lines.',
    },
    {
      key: 'cloudMaxTerms',
      label: 'Word cloud: most words to lay out',
      type: 'number',
      default: 120,
      scope: 'workspace',
      description: 'The cloud layout runs on the main thread, so this is capped — a very high number makes the window unresponsive while it settles.',
    },
  ]);

  api.ui.registerHeaderButton({
    id: 'settings:open',
    label: 'Settings',
    icon: 'settings',
    tooltip: 'Workspace and plugin settings',
    variant: 'secondary',
    onClick: () => api.ui.openSettings(),
  });

  // Drag-and-drop a `secrets.txt` to load the device-local secrets store.
  // Registered first among built-ins so it claims the drop before the CSV/JSON
  // importers; it only handles a file literally named `secrets.txt` and returns
  // false for anything else, so normal data imports are unaffected.
  api.ui.registerDropHandler(async (event, api) => {
    const files = Array.from(event.dataTransfer?.files ?? []);
    const file = files.find((f) => f.name.toLowerCase() === 'secrets.txt');
    if (!file) return false;

    const text = await file.text();
    const count = Object.keys(parseSecrets(text)).length;

    if (readSecretsText().trim().length > 0) {
      const ok = await api.ui.dialogs.confirm(`Replace your current secrets with ${count} secret${count === 1 ? '' : 's'} from "${file.name}"?`, 'Import secrets');
      if (!ok) return true; // drop consumed, but the user declined
    }

    writeSecretsText(text);
    api.ui.dialogs.toast(`Imported ${count} secret${count === 1 ? '' : 's'}.`, {
      kind: 'success',
      title: 'Secrets',
    });
    return true;
  });
}
